import {srcRef} from "/@lib@/wsp/src.js";

/**
 * Librairie pour les dynamicGenerator d'un chain.
 */

/**
 * Reconstruction des données d'une hierarchie d'attributs data-origin.
 */
export interface JOrigin {
	srcRef: srcRef
	oriPath: string
	lastModif: number
}

export namespace DYNGEN {

	/** Retrouve les info d'origne en remontant les ancêtres d'un élément DOM. */
	export function getOriginFrom(elt: Element): JOrigin {
		const origin = {} as JOrigin;
		const oriPath: string[] = [];
		do {
			const ori = elt.getAttribute("data-origin");
			if (ori) {
				if (ori.charAt(0) === '@') {
					const sep = ori.indexOf('?', 1);
					origin.srcRef = ori.substring(1, sep);
					const sep2 = ori.indexOf('#', sep);
					origin.lastModif = parseInt(sep2 > 0 ? ori.substring(sep + 1, sep2) : ori.substring(sep + 1));
					if (sep2 > 0) oriPath.push(ori.substring(sep2 + 1));
					origin.oriPath = oriPath.reverse().join("/");
					return origin;
				} else {
					oriPath.push(ori);
				}
			}
		} while ((elt = elt.parentElement));
		return origin;
	}

	export function getSrcRefFrom(elt: Element): srcRef {
		do {
			const ori = elt.getAttribute("data-origin");
			if (ori) {
				if (ori.charAt(0) === '@') {
					return ori.substring(1, ori.indexOf('?', 1));
				}
			}
		} while ((elt = elt.parentElement));
		return null;
	}

	/**
	 * Parcourt un arbre DOM pour trouver tous les éléments qui définissent un srcRef.
	 */
	export function findSrcRefs(from: Element, cb: (elt: Element, srcRef: srcRef, lastModif: number) => void | 'stop') {
		const tw = from.ownerDocument.createTreeWalker(from, NodeFilter.SHOW_ELEMENT);
		do {
			const ori = (tw.currentNode as Element).getAttribute("data-origin");
			if (ori && ori.charAt(0) === '@') {
				const sep = ori.indexOf('?', 1);
				const srcRef = ori.substring(1, sep);
				const sep2 = ori.indexOf('#', sep);
				const lastModif = parseInt(sep2 > 0 ? ori.substring(sep + 1, sep2) : ori.substring(sep + 1));
				if (cb(tw.currentNode as Element, srcRef, lastModif) === 'stop') return;
			}
		} while (tw.nextNode());
	}

	export function updateOrigin(from: Element, srcRef: srcRef, path: number[], delta: number) {
		const depthDelta = path.length - 1;
		const fragDelta = path[depthDelta];
		//depth = profondeur courante de l'oriPath
		// null = hors du srcRef recherché,
		// 0 : on est à la racine du document srcRef
		// 1 : on est dans path[0]
		// 2 : on est dans path[0]/path[1]
		// ...
		// -1 : on est à la profondeur 1 du path, mais hors de path[0]
		// -2 : on est à la profondeur 2 du path, mais hors de path[0]/path[1]
		// -3 : on est à la profondeur 3 du path, mais hors du path car path.length == 2
		// ...
		const depths: Array<number | null> = [];
		let lastDepth = null as number | null;
		let elt = from;

		function findNewDepth(ori: string, offsetPath: number): number {
			const frags = ori.substring(offsetPath).split('/');
			if (lastDepth < 0) {
				//On est hors du path recherché
				return lastDepth - frags.length;
			} else {
				let currentDepth = lastDepth;
				for (let i = 0; i < frags.length; i++) {
					const frag = parseInt(frags[i], 10);
					if (currentDepth === depthDelta) {
						if (frag >= fragDelta) {
							//On doit réécrire l'attribut.
							frags[i] = (frag + delta).toString();
							elt.setAttribute("data-origin", ori.substring(0, offsetPath) + frags.join('/'));
						}
						return -(lastDepth + frags.length); //on sort du path recherché
					}
					if (frag !== path[currentDepth]) return -(lastDepth + frags.length); //on sort du path recherché
					//On s'enfonce dans le bon path
					currentDepth++;
				}
				return currentDepth;
			}
		}

		while (elt) {
			let newDepth = lastDepth; //par défaut, on va garder le même depth de l'oriPath.
			const ori = elt.getAttribute("data-origin");
			if (ori) {
				if (lastDepth === null) {
					//on n'est pas dans le srcRef
					if (ori.charAt(0) === '@') {
						const sep = ori.indexOf('?', 1);
						if (srcRef === ori.substring(1, sep)) {
							//On a trouvé le srcRef.
							lastDepth = 0; //on se place à la racine
							//on s'enfonce dans le path
							const sep2 = ori.indexOf('#', sep);
							newDepth = sep2 > 0 ? findNewDepth(ori, sep2 + 1) : 0;
						}
					}
				} else {
					//on est dans le srcRef
					if (ori.charAt(0) === '@') {
						//on change de srcRef,
						const sep = ori.indexOf('?', 1);
						if (srcRef === ori.substring(1, sep)) {
							//On a trouvé le srcRef.
							lastDepth = 0; //on se place à la racine
							//on s'enfonce dans le path
							const sep2 = ori.indexOf('#', sep);
							newDepth = sep2 > 0 ? findNewDepth(ori, sep2 + 1) : 0;
						} else {
							newDepth = null;
						}
					} else {
						//on s'enfonce dans le path
						newDepth = findNewDepth(ori, 0);
					}
				}
			}
			if (elt.firstElementChild) {
				depths.push(newDepth);
				lastDepth = newDepth;
				elt = elt.firstElementChild;
			} else {
				if (elt === from) return;
				while (!elt.nextElementSibling) {
					elt = elt.parentElement;
					if (elt === from) return;
					depths.splice(depths.length - 1, 1);
					lastDepth = depths[depths.length - 1];
				}
				elt = elt.nextElementSibling;
			}
		}
	}

	/**
	 * Encoding de fragments URL selon une règle complémentaire pour éviter
	 * plusieurs problèmes (avec flash, sécutité tomcat, apache et proxy...)
	 *
	 * Règles d'encoding :
	 *
	 * Not encoded :
	 * [0-9a-zA-Z] - _ .
	 *
	 * Translation :
	 * /  -> !
	 * :  -> (
	 * @  -> )
	 *
	 * Simple escaping :
	 * !  -> '!
	 * (  -> '(
	 * )  -> ')
	 * '  -> ''
	 * *  -> '*
	 *
	 * For all other characters : UTF8 escaping
	 * each byte  -> *XX with X = [0-9A-F]
	 */
	export function encodeUrl2(val: string): string {
		return encodeURIComponent(val.replace(/[\/:@\!\(\)\'\*]/g, encodeUrl2Esc)).replace(/\%/g, "*");
	}

	function encodeUrl2Esc(match: string) {
		switch (match) {
		case "/":
			return "!";
		case ":":
			return "(";
		case "@":
			return ")";
		case "!":
			return "'!";
		case "(":
			return "'(";
		case ")":
			return "')";
		case "'":
			return "''";
		case "*":
			return "'*";
		}
	}
}
